import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// 1. IMPORT LOCAL IMAGES
import image1 from '../../../assets/Images/Carousel1.jpg';
import image2 from '../../../assets/Images/Carousel2.jpg';
import image3 from '../../../assets/Images/Carousel3.jpg';
import image4 from '../../../assets/Images/Carousel4.jpg';


// Define a simple Tailwind configuration to simulate the user's colors
const tailwindConfig = {
    theme: {
        extend: {
            colors: {
                'primary': '#DC2626', // Red-600 (used for accents)
                'secondary': '#1F2937', // Gray-800 (used for darker backgrounds/text)
                'dark-text': '#1F2937', // ALIAS FOR SECONDARY FOR CLARITY IN THE LOGO TEXT
                'accent': '#FDE047', // Yellow-300
            },
            fontFamily: {
                sans: ['Inter', 'sans-serif'],
                heading: ['Montserrat', 'sans-serif'],
            }
        },
    },
};

// --- Reliable Image and Dynamic Content Data ---
const carouselSlides = [
    {
        url: image1, 
        preHeading: "Signature Interior Design", 
        // 1. CHANGE: Using 'jiggle-and-pop' and 'wavy-underline'
        heading: "Elevate Your Living Spaces with <span class='text-primary jiggle-and-pop wavy-underline'>Exquisite Design.</span>",
        primaryCtaText: "View Our Portfolio",
        primaryCtaLink: "projects",
        secondaryCtaText: "Get a Free Consultation",
        secondaryCtaLink: "contact",
    },
    {
        url: image2, 
        alt: "Modern, open-plan kitchen with wood accents",
        preHeading: "Culinary Dreams, Realized", 
        // 1. CHANGE: Using 'jiggle-and-pop' and 'wavy-underline'
        heading: "Crafting Kitchens of <span class='text-primary jiggle-and-pop wavy-underline'>Unmatched Beauty</span> & Efficiency.",
        primaryCtaText: "Explore Kitchen Designs",
        primaryCtaLink: "services",
        secondaryCtaText: "Request a Quote",
        secondaryCtaLink: "contact"
    },
    {
        url: image3, 
        alt: "Bright bedroom with minimalist decor",
        preHeading: "Sanctuaries of Serenity", 
        // 1. CHANGE: Using 'jiggle-and-pop' and 'wavy-underline'
        heading: "Transform Your Bedroom into a <span class='text-primary jiggle-and-pop wavy-underline'>Peaceful Retreat.</span>",
        primaryCtaText: "Bedroom Inspirations",
        primaryCtaLink: "services",
        secondaryCtaText: "Book a Design Session",
        secondaryCtaLink: "contact"
    },
    {
        url: image4, 
        alt: "Large, luxury industrial loft with high ceilings",
        preHeading: "Innovation in Every Detail", 
        // 1. CHANGE: Using 'jiggle-and-pop' and 'wavy-underline'
        heading: "Innovative Design for <span class='text-primary jiggle-and-pop wavy-underline'>Dynamic Urban Living.</span>",
        primaryCtaText: "View Loft Projects",
        primaryCtaLink: "services",
        secondaryCtaText: "Talk to an Expert",
        secondaryCtaLink: "contact"
    }
];
// --- End of Image and Content Data ---

// --- CONFIGURATION CONSTANTS ---
const SLIDE_INTERVAL_NORMAL = 7000; 
const TRANSITION_DURATION = '1000ms'; 
// --- END CONFIGURATION CONSTANTS ---


const Hero = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    // --- Navigation Handler with Transition Logic ---
    const navigateToSlide = useCallback((newSlideIndex) => {
        setCurrentSlide(newSlideIndex);
    }, []);

    const nextSlide = useCallback(() => {
        const nextIndex = currentSlide === carouselSlides.length - 1 ? 0 : currentSlide + 1;
        navigateToSlide(nextIndex);
    }, [currentSlide, navigateToSlide]);

    const prevSlide = useCallback(() => {
        const prevIndex = currentSlide === 0 ? carouselSlides.length - 1 : currentSlide - 1;
        navigateToSlide(prevIndex);
    }, [currentSlide, navigateToSlide]);
    
    // --- Auto-advance functionality ---
    useEffect(() => {
        if (isPaused) {
            return;
        }

        const duration = SLIDE_INTERVAL_NORMAL; 
        
        const interval = setInterval(nextSlide, duration); 
        return () => clearInterval(interval);

    }, [nextSlide, isPaused]); 

    // --- Styling Classes ---
    const heroOverlayClasses = 'absolute inset-0 bg-gray-900 opacity-40';
    const buttonBaseClasses = 'absolute top-1/2 -translate-y-1/2 p-2 sm:p-3 z-20 text-white bg-black bg-opacity-20 md:bg-opacity-30 hover:bg-opacity-50 transition duration-300 rounded-full cursor-pointer';

    // --- LOGO TEXT COMPONENT (Modified for the new content) ---
    const LogoText = ({ data, index }) => {
        if (!data) return null;

        return (
            <p 
                key={`preheading-key-${index}`} 
                className={`
                    text-center font-heading tracking-widest leading-none mb-4 sm:mb-4 
                    text-base sm:text-2xl lg:text-3xl font-extrabold uppercase text-outline-effect
                `}
            >
                {data}
            </p>
        );
    };
    // --- END LOGO TEXT COMPONENT ---
    
    // --- SLIDE CONTENT COMPONENT ---
    const SlideContent = ({ slide, index }) => (
        <div 
            key={`content-${index}`}
            className="single-slide-content relative flex flex-col items-center justify-center h-full w-full px-4"
        >
            <div className="text-center max-w-3xl w-full"> 
                {/* --- PRE-HEADING (Signature Interior Design / Culinary Dreams, etc.) --- */}
                <LogoText data={slide.preHeading} index={index} /> 

                <h1 
                    className="text-xl sm:text-4xl lg:text-5xl font-heading font-extrabold text-white mb-5 leading-tight text-shadow-lg" 
                    dangerouslySetInnerHTML={{ __html: slide.heading }}
                >
                </h1>
                
                {/* --- SUBHEADING --- */}
                {slide.subheading && (
                    <p className="text-sm sm:text-base text-gray-200 mb-6 max-w-2xl mx-auto subheading-neon"> 
                        {slide.subheading}
                    </p>
                )}
                
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center"> 
                    {/* CTA 1: Primary Button */}
                    <a href={slide.primaryCtaLink} 
                        className="btn-premium bg-primary hover:bg-red-700 text-white px-4 sm:px-6 py-2 rounded-full font-semibold uppercase tracking-wider text-xs transition duration-300 shadow-xl">
                        {slide.primaryCtaText}
                    </a>
                    {/* CTA 2: Secondary Button */}
                    <a href={slide.secondaryCtaLink} 
                        className="btn-premium bg-transparent border-2 border-white text-white hover:bg-white hover:text-secondary px-4 sm:px-6 py-2 rounded-full font-semibold uppercase tracking-wider text-xs transition duration-300">
                        {slide.secondaryCtaText}
                    </a>
                </div>
            </div>
        </div>
    );
    // --- END SLIDE CONTENT COMPONENT ---

    return (
        <div className="font-sans" style={{ ...tailwindConfig.theme.fontFamily }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&family=Montserrat:wght@400;600;700;900;900&display=swap');
                
                .btn-premium {
                    transform: translateY(0);
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }
                .btn-premium:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 10px rgba(0, 0, 0, 0.2);
                }

                .text-shadow-lg {
                    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.6);
                }
                
                /* --- Dramatic Red/White Outline Effect for pre-headings --- */
                .text-outline-effect {
                    color: white; 
                    text-shadow: 
                        -1px -1px 0 #DC2626,  
                        1px -1px 0 #DC2626,
                        -1px 1px 0 #DC2626,
                        1px 1px 0 #DC2626,
                        0 0 10px rgba(0, 0, 0, 0.8);
                    filter: drop-shadow(0 0 5px rgba(220, 38, 38, 0.5));
                }

                /* --- ENHANCED STYLING: Jiggle and Pop (Scale and Subtle Vertical Movement) --- */
                .jiggle-and-pop {
                    display: inline-block;
                    position: relative;
                    /* REMOVED: transition: transform 0.5s... (as we use keyframe animation now) */
                    
                    /* Animation for automatic pop-up/down effect */
                    animation: pop-jiggle 3s ease-in-out infinite alternate; /* Reduced time to 3s for more energy */
                    white-space: nowrap; 
                }
                
                .jiggle-and-pop:hover {
                    /* Kept hover effect for user interaction */
                    transform: scale(1.05) translateY(-2px);
                }

                /* MODIFIED KEYFRAMES: Increased scale for a more noticeable 'pop' */
                @keyframes pop-jiggle {
                    0%, 100% {
                        transform: scale(1) translateY(0);
                    }
                    50% {
                        /* Pop-up and slightly larger */
                        transform: scale(1.03) translateY(-4px); 
                    }
                }
                
                /* --- NEW STYLING: Wavy Underline Effect --- */
                .wavy-underline {
                    display: inline-block;
                }

                .wavy-underline::after {
                    content: '';
                    display: block;
                    width: 100%;
                    height: 2px;
                    background-color: #DC2626; /* Primary Red */
                    
                    /* Wave/Pop Animation - Set to the same duration and timing as the text animation */
                    animation: wave-line 3s ease-in-out infinite alternate;
                    transform-origin: bottom center;
                    margin-top: 2px;
                }
                
                @keyframes wave-line {
                    0%, 100% {
                        transform: scaleX(0.98) translateY(0);
                    }
                    50% {
                        /* Subtle wave effect to complement the text's pop */
                        transform: scaleX(1.01) translateY(1px); 
                    }
                }

                
                /* --- Subtle Neon Glow for Subheadings (kept for the subheading) --- */
                .subheading-neon {
                    color: #fff;
                    font-style: italic;
                    font-weight: 300;
                    text-shadow: 
                        0 0 5px rgba(255, 255, 255, 0.6),
                        0 0 10px rgba(255, 255, 255, 0.4),
                        0 0 15px rgba(0, 0, 0, 0.5); 
                    
                    animation: neon-pulse 5s ease-in-out infinite alternate;
                }

                @keyframes neon-pulse {
                    0% { opacity: 1; text-shadow: 0 0 5px #fff; }
                    100% { opacity: 0.9; text-shadow: 0 0 8px #fff, 0 0 15px rgba(255, 255, 255, 0.2); }
                }

                
                /* --- SLIDE TRANSITION CSS (OPTIMIZED) --- */
                
                .slides-container, .content-slide-wrapper {
                    width: ${carouselSlides.length * 100}%;
                    display: flex;
                    transition: transform ${TRANSITION_DURATION} ease-in-out;
                    height: 100%;
                    will-change: transform;
                    transform: translateZ(0);
                }
                
                .single-slide, .single-slide-content-wrapper {
                    width: calc(100% / ${carouselSlides.length});
                    flex-shrink: 0;
                }
                
                .content-slide-wrapper {
                    position: absolute;
                    top: 0;
                    left: 0;
                    align-items: center;
                    justify-content: flex-start;
                    z-index: 7;
                }
            `}</style>
            
            <section id="home" 
                className="relative h-[65vh] sm:h-[75vh] flex items-center justify-center overflow-hidden bg-secondary pt-16 sm:pt-20"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
            > 
                
                {/* 1. Image Carousel Background Container */}
                <div className="absolute inset-0 overflow-hidden">
                    
                    {/* SLIDES CONTAINER: This element moves horizontally (Images) */}
                    <div 
                        className="slides-container"
                        style={{ transform: `translateX(-${currentSlide * (100 / carouselSlides.length)}%)` }}
                    >
                        {carouselSlides.map((slide, index) => (
                            <div 
                                key={`img-${index}`} 
                                className={`single-slide w-full h-full bg-cover bg-center relative`}
                                style={{ 
                                    backgroundImage: `url(${slide.url})`,
                                }}
                            >
                                {/* Dark Overlay moves with the image */}
                                <div className={heroOverlayClasses} style={{ zIndex: 6 }}></div>
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* 2. Content Carousel Container */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div 
                        className="content-slide-wrapper"
                        style={{ transform: `translateX(-${currentSlide * (100 / carouselSlides.length)}%)` }}
                    >
                        {carouselSlides.map((slide, index) => (
                            <div 
                                key={`content-wrapper-${index}`}
                                className="single-slide-content-wrapper h-full flex items-center justify-center pointer-events-auto"
                            >
                                <SlideContent slide={slide} index={index} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Navigation Buttons (Static) */}
                <button 
                    onClick={prevSlide} 
                    className={`${buttonBaseClasses} left-2 sm:left-4`}
                    aria-label="Previous slide"
                    style={{ zIndex: 10 }}
                >
                    <ChevronLeft size={24} className="sm:size-8" />
                </button>
                <button 
                    onClick={nextSlide} 
                    className={`${buttonBaseClasses} right-2 sm:right-4`}
                    aria-label="Next slide"
                    style={{ zIndex: 10 }}
                >
                    <ChevronRight size={24} className="sm:size-8" />
                </button>
                
                {/* Dots Indicator (Static) */}
                <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2" style={{ zIndex: 8 }}>
                    {carouselSlides.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => navigateToSlide(index)}
                            className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                index === currentSlide ? 'bg-primary scale-125' : 'bg-white opacity-50 hover:opacity-80'
                            }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>

            </section>
            
        </div>
    );
};

export default Hero;
